import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitBranch, Plus, X, AlertCircle, RefreshCw, Play, Sun, Moon,
  Mail, MessageCircle, Smartphone, MessagesSquare, Globe, Camera, Target,
  Clock, MousePointer, Send, CheckCircle2, Filter, ChevronRight,
  Zap, History, Trash2, GitMerge, Download, TrendingUp, BarChart3,
} from 'lucide-react';

import Toast from '../components/Toast';
import useAutoLogout from '../hooks/useAutoLogout';
import { useTheme, useFeatureFlags } from '../components/Navbar';
import API_BASE from '../config';

/* ------------------------------------------------------------------ *
 * Theme tokens — mirror of the private TK object in Navbar.jsx.
 * ------------------------------------------------------------------ */
const TK = {
  light: {
    canvas: '#f7f5ff', surface: '#ffffff', surfaceAlt: '#faf9ff',
    border: '#ece8fb', borderStrong: '#ddd6f5',
    text: '#1a1530', textSoft: '#6b6580', textFaint: '#9b95ac',
    brand: '#5B4EE8', brandSoft: 'rgba(91,78,232,0.10)',
    shadow: '0 1px 3px rgba(26,21,48,0.06), 0 8px 24px rgba(26,21,48,0.04)',
    overlay: 'rgba(26,21,48,0.55)',
  },
  dark: {
    canvas: '#0a0a0f', surface: '#13131c', surfaceAlt: '#16161f',
    border: '#22222e', borderStrong: '#2c2c3a',
    text: '#f4f4f5', textSoft: '#a1a1aa', textFaint: '#71717a',
    brand: '#7C72FF', brandSoft: 'rgba(124,114,255,0.14)',
    shadow: '0 1px 3px rgba(0,0,0,0.4)',
    overlay: 'rgba(0,0,0,0.66)',
  },
};

/* Channel registry — lucide-react 0.383.0 has no Facebook/Instagram → Camera/Globe. */
const CH = {
  whatsapp:  { label: 'WhatsApp',     Icon: MessageCircle,  color: '#25D366', kind: 'message' },
  email:     { label: 'Email',        Icon: Mail,           color: '#3b82f6', kind: 'message' },
  sms:       { label: 'SMS',          Icon: Smartphone,     color: '#f59e0b', kind: 'message' },
  rcs:       { label: 'RCS',          Icon: MessagesSquare, color: '#8b5cf6', kind: 'message' },
  instagram: { label: 'Instagram',    Icon: Camera,         color: '#E1306C', kind: 'message' },
  facebook:  { label: 'Facebook Ads', Icon: Globe,          color: '#1877F2', kind: 'ads' },
  google:    { label: 'Google Ads',   Icon: Target,         color: '#EA4335', kind: 'ads' },
};
const CHANNEL_KEYS = Object.keys(CH);
const CHANNEL_FLAG_MAP = { whatsapp: 'whatsapp_campaigns', email: 'email_journeys', sms: 'sms_channel', rcs: 'rcs_channel', instagram: 'instagram_channel', facebook: 'facebook_sync', google: 'google_sync' };

const STATUS_MAP = {
  active:   { color: '#10b981', label: 'Active' },
  inactive: { color: '#71717a', label: 'Inactive' },
  draft:    { color: '#f59e0b', label: 'Draft' },
  paused:   { color: '#3b82f6', label: 'Paused' },
  complete: { color: '#10b981', label: 'Complete' },
};

const NODE_TYPES = [
  { value: 'message',   label: 'Send message' },
  { value: 'wait',      label: 'Wait' },
  { value: 'condition', label: 'Condition' },
];

const TRIGGER_EVENTS = [
  'user_signup', 'purchase_completed', 'cart_abandoned',
  'page_viewed', 'inactivity_7d', 'appointment_booked', 'custom_event',
];

/* ------------------------------------------------------------------ */
const num = (n) => (n == null ? 0 : parseInt(n, 10) || 0);
const titleize = (s) =>
  (s || '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/* Mobile responsive hook — JS-driven, per project pattern */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

/* Body scroll lock while an overlay is open (mobile) */
function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [active]);
}

function AnimatedNumber({ value }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 600; const from = 0;
    const tick = (t) => {
      const k = Math.min(1, (t - start) / dur);
      setShown(Math.round(from + (value - from) * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{shown}</>;
}

function Skel({ tk, h = 48, style }) {
  return (
    <div className="mts-shimmer"
      style={{ height: h, borderRadius: 12, background: tk.surfaceAlt, ...style }} />
  );
}

function ThemeToggle({ tk, theme, setTheme, isMobile }) {
  return (
    <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      aria-label="Toggle theme"
      style={{
        width: isMobile ? 42 : 38, height: isMobile ? 42 : 38, borderRadius: 11, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: tk.surface, border: `1px solid ${tk.border}`, color: tk.textSoft, flexShrink: 0,
      }}>
      {theme === 'dark' ? <Sun style={{ width: 16, height: 16 }} /> : <Moon style={{ width: 16, height: 16 }} />}
    </button>
  );
}

function StatusBadge({ status, tk }) {
  const s = STATUS_MAP[(status || '').toLowerCase()] ||
    { color: tk.textFaint, label: status || '—' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
      background: `${s.color}1f`, color: s.color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: s.color, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

function ChannelPill({ channel, tk, small }) {
  const c = CH[channel] || { label: channel, Icon: GitBranch, color: tk.textFaint };
  const { Icon } = c;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: small ? 10 : 11, fontWeight: 700,
      padding: small ? '2px 7px' : '3px 9px', borderRadius: 999,
      background: `${c.color}1a`, color: c.color, whiteSpace: 'nowrap',
    }}>
      <Icon style={{ width: small ? 11 : 13, height: small ? 11 : 13 }} />
      {c.label}
    </span>
  );
}

function StatCard({ label, value, color, tk, loading, isMobile }) {
  return (
    <div style={{
      background: tk.surface, border: `1px solid ${tk.border}`,
      borderRadius: 18, padding: isMobile ? 16 : 20, boxShadow: tk.shadow,
    }}>
      {loading
        ? <Skel tk={tk} h={isMobile ? 26 : 30} style={{ width: 70, marginBottom: 6 }} />
        : <p style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color, lineHeight: 1, margin: 0 }}>
            <AnimatedNumber value={value} />
          </p>}
      <p style={{ color: tk.textSoft, fontSize: 12, marginTop: 8, fontWeight: 600 }}>{label}</p>
    </div>
  );
}

function Metric({ tk, Icon, label, value, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
      borderRadius: 9, padding: '5px 10px',
    }}>
      <Icon style={{ width: 13, height: 13, color: color || tk.textSoft }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: color || tk.text }}>{value}</span>
      <span style={{ fontSize: 11, color: tk.textFaint }}>{label}</span>
    </div>
  );
}

/* ================================================================== */
/* Reports panel — built for campaign managers / MIS: top-line KPIs,   */
/* per-channel effectiveness, 14-day trend, and CSV export.            */
function ReportsPanel({ tk, theme, isMobile, summary, channels, trend, loading, onRefresh, onExport, exporting }) {
  const maxTrendVal = Math.max(1, ...trend.map(t => t.total));
  const maxChannelSent = Math.max(1, ...channels.map(c => c.total_sent));

  const kpis = summary ? [
    { label: 'Total evaluations', value: summary.total_evaluations, color: tk.brand },
    { label: 'Conversion rate', value: `${summary.conversion_rate}%`, color: '#10b981' },
    { label: 'Fallback rate', value: `${summary.fallback_rate}%`, color: '#f59e0b' },
    { label: 'Failed sends', value: summary.failed_count, color: '#ef4444' },
  ] : [];

  return (
    <div>
      {/* header row */}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 18,
      }}>
        <div>
          <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: tk.text, margin: 0 }}>
            Omnichannel Fallback Performance
          </h2>
          <p style={{ color: tk.textSoft, fontSize: 12, margin: '3px 0 0' }}>
            How conversion-based channel routing is performing across all journeys
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={onRefresh} disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: isMobile ? '10px 14px' : '8px 14px', borderRadius: 10,
              fontSize: 12.5, fontWeight: 700, color: tk.textSoft,
              background: tk.surface, border: `1px solid ${tk.border}`, cursor: 'pointer',
              opacity: loading ? 0.6 : 1, flex: isMobile ? 1 : 'none', justifyContent: 'center' }}>
            <RefreshCw className={loading ? 'mts-spin' : ''} style={{ width: 14, height: 14 }} /> Refresh
          </button>
          <button onClick={onExport} disabled={exporting}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: isMobile ? '10px 14px' : '8px 14px', borderRadius: 10,
              fontSize: 12.5, fontWeight: 700, color: '#fff', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${tk.brand}, ${theme === 'dark' ? '#9d8bff' : '#7b5bf0'})`,
              opacity: exporting ? 0.6 : 1, flex: isMobile ? 1 : 'none', justifyContent: 'center' }}>
            <Download style={{ width: 14, height: 14 }} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      {loading && !summary ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? 10 : 14, marginBottom: 18 }}>
          {Array(4).fill(null).map((_, i) => <Skel key={i} tk={tk} h={70} />)}
        </div>
      ) : summary ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? 10 : 14, marginBottom: 18 }}>
          {kpis.map(k => (
            <div key={k.label} style={{ background: tk.surface, border: `1px solid ${tk.border}`,
              borderRadius: 16, padding: isMobile ? 14 : 18, boxShadow: tk.shadow }}>
              <p style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: k.color, margin: 0, lineHeight: 1 }}>{k.value}</p>
              <p style={{ fontSize: 11.5, color: tk.textSoft, marginTop: 7, marginBottom: 0, fontWeight: 600 }}>{k.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 16,
          padding: '40px 20px', textAlign: 'center', marginBottom: 18 }}>
          <BarChart3 style={{ width: 32, height: 32, color: tk.borderStrong, margin: '0 auto 10px' }} />
          <p style={{ color: tk.textSoft, fontSize: 13, margin: 0 }}>
            No fallback evaluations recorded yet. Data appears here once condition-node journeys start running.
          </p>
        </div>
      )}

      {/* two-column: channel effectiveness + trend */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>

        {/* Channel effectiveness */}
        <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 16,
          padding: isMobile ? 14 : 18, boxShadow: tk.shadow }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em',
            color: tk.textFaint, margin: '0 0 14px' }}>Channel effectiveness</p>
          {channels.length === 0 ? (
            <p style={{ color: tk.textSoft, fontSize: 12.5, margin: 0 }}>No channel data yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {channels.map(c => {
                const chInfo = CH[c.channel] || { label: c.channel, Icon: GitBranch, color: tk.textFaint };
                const Icon = chInfo.Icon;
                const barWidth = Math.max(4, (c.total_sent / maxChannelSent) * 100);
                return (
                  <div key={c.channel}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: tk.text }}>
                        <Icon style={{ width: 13, height: 13, color: chInfo.color }} /> {chInfo.label}
                      </span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#10b981' }}>{c.conversion_rate}% conv.</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: tk.surfaceAlt, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${barWidth}%`, borderRadius: 999, background: chInfo.color }} />
                    </div>
                    <p style={{ fontSize: 10.5, color: tk.textFaint, margin: '4px 0 0' }}>
                      {c.total_sent} sent · {c.conversions} converted · {c.failures} failed
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 14-day trend */}
        <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 16,
          padding: isMobile ? 14 : 18, boxShadow: tk.shadow }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em',
            color: tk.textFaint, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <TrendingUp style={{ width: 12, height: 12 }} /> 14-day evaluation trend
          </p>
          {trend.length === 0 ? (
            <p style={{ color: tk.textSoft, fontSize: 12.5, margin: 0 }}>No trend data yet.</p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
              {trend.map((t) => {
                const h = Math.max(3, (t.total / maxTrendVal) * 100);
                const convH = t.total > 0 ? (t.converted / t.total) * h : 0;
                return (
                  <div key={t.date} style={{ flex: 1, display: 'flex', flexDirection: 'column',
                    justifyContent: 'flex-end', height: '100%', minWidth: 0 }}
                    title={`${t.date}: ${t.total} evaluated, ${t.converted} converted, ${t.fallback} fallback`}>
                    <div style={{ width: '100%', height: `${h}%`, borderRadius: '4px 4px 0 0',
                      background: tk.surfaceAlt, border: `1px solid ${tk.border}`, position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                        height: `${convH}%`, background: '#10b981' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: tk.textSoft }}>
              <span style={{ width: 8, height: 8, borderRadius: 3, background: '#10b981' }} /> Converted
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: tk.textSoft }}>
              <span style={{ width: 8, height: 8, borderRadius: 3, background: tk.surfaceAlt, border: `1px solid ${tk.border}` }} /> Fallback / pending
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
export default function Journeys() {
  const navigate = useNavigate();
  useAutoLogout();
  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();
  const { flags } = useFeatureFlags();
  const visibleChannelKeys = CHANNEL_KEYS.filter((k) => flags[CHANNEL_FLAG_MAP[k]] !== false);
  const tk = TK[theme] || TK.light;

  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [triggering, setTriggering] = useState(null);

  // filters
  const [fChannel, setFChannel] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fTrigger, setFTrigger] = useState('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile collapse

  // detail drawer
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // build modal
  const [buildOpen, setBuildOpen] = useState(false);

  // view tab: journeys list vs fallback reports (MIS/campaign-manager view)
  const [view, setView] = useState('journeys'); // 'journeys' | 'reports'

  // reports data
  const [reportSummary, setReportSummary] = useState(null);
  const [reportChannels, setReportChannels] = useState([]);
  const [reportTrend, setReportTrend] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useBodyScrollLock(isMobile && (buildOpen || !!detail));

  const showToast = (msg, variant = 'green') => setToast({ msg, variant });
  const authHeaders = () => {
    const token = localStorage.getItem('martech_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchReports = useCallback(async () => {
    setReportLoading(true);
    try {
      const [sRes, cRes, tRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/journeys/reports/summary`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/v1/journeys/reports/channel-performance`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/v1/journeys/reports/trend?days=14`, { headers: authHeaders() }),
      ]);
      const [sData, cData, tData] = await Promise.all([sRes.json(), cRes.json(), tRes.json()]);
      if (sData.success) setReportSummary(sData.summary);
      if (cData.success) setReportChannels(cData.channels || []);
      if (tData.success) setReportTrend(tData.trend || []);
    } catch { showToast('Could not load reports', 'red'); }
    setReportLoading(false);
  }, []);

  useEffect(() => { if (view === 'reports') fetchReports(); }, [view, fetchReports]);

  const exportReportCsv = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/journeys/reports/export`, { headers: authHeaders() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Export failed');
      const rows = d.rows || [];
      if (rows.length === 0) { showToast('No data to export', 'blue'); setExporting(false); return; }
      const headers = Object.keys(rows[0]);
      const csvLines = [
        headers.join(','),
        ...rows.map(row => headers.map(h => {
          const val = row[h] == null ? '' : String(row[h]).replace(/"/g, '""');
          return /[",\n]/.test(val) ? `"${val}"` : val;
        }).join(',')),
      ];
      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `journey-fallback-report-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Report exported');
    } catch (e) { showToast(e.message || 'Export failed', 'red'); }
    setExporting(false);
  };

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    if (fChannel !== 'all') q.set('channel', fChannel);
    if (fStatus !== 'all') q.set('status', fStatus);
    if (fTrigger !== 'all') q.set('trigger_type', fTrigger);
    if (fFrom) q.set('from', fFrom);
    if (fTo) q.set('to', fTo);
    const s = q.toString();
    return s ? `?${s}` : '';
  }, [fChannel, fStatus, fTrigger, fFrom, fTo]);

  const fetchJourneys = useCallback(async () => {
    if (!localStorage.getItem('martech_token')) { navigate('/login'); return; }
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/journeys${buildQuery()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) setJourneys(data.journeys || []);
      else setError(data.error || 'Could not load journeys');
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [navigate, buildQuery]);

  useEffect(() => { fetchJourneys(); }, [fetchJourneys]);

  const stats = useMemo(() => {
    const total = journeys.length;
    const active = journeys.filter(j => (j.status || '').toLowerCase() === 'active').length;
    const enrollments = journeys.reduce((s, j) => s + num(j.enrollments), 0);
    const channels = new Set();
    journeys.forEach(j => (j.channels || []).forEach(c => channels.add(c)));
    return { total, active, enrollments, channels: channels.size };
  }, [journeys]);

  const openDetail = async (id) => {
    setDetailLoading(true); setDetail({ loadingId: id });
    try {
      const res = await fetch(`${API_BASE}/api/v1/journeys/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.success) setDetail(data);
      else { setDetail(null); showToast(data.error || 'Could not open journey', 'red'); }
    } catch { setDetail(null); showToast('Network error', 'red'); }
    setDetailLoading(false);
  };

  const handleTrigger = async (id) => {
    setTriggering(id);
    try {
      const r = await fetch(`${API_BASE}/api/v1/journeys/${id}/trigger`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: true }),
      });
      const d = await r.json();
      if (r.ok && d.success !== false) showToast(d.message || 'Journey triggered');
      else showToast(d.error || 'Could not trigger journey', 'red');
    } catch { showToast('Network error', 'red'); }
    setTriggering(null);
  };

  const resetFilters = () => {
    setFChannel('all'); setFStatus('all'); setFTrigger('all'); setFFrom(''); setFTo('');
  };
  const filtersActive = fChannel !== 'all' || fStatus !== 'all' ||
    fTrigger !== 'all' || fFrom || fTo;
  const activeFilterCount =
    (fChannel !== 'all' ? 1 : 0) + (fStatus !== 'all' ? 1 : 0) +
    (fTrigger !== 'all' ? 1 : 0) + (fFrom ? 1 : 0) + (fTo ? 1 : 0);

  const selStyle = {
    background: tk.surface, border: `1px solid ${tk.border}`, color: tk.text,
    fontSize: isMobile ? 16 : 13, fontWeight: 600, padding: isMobile ? '10px 12px' : '8px 12px',
    borderRadius: 10, outline: 'none',
    width: isMobile ? '100%' : 'auto', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: tk.canvas, color: tk.text, maxWidth: '100vw', overflowX: 'hidden' }}>
      <style>{`
        .mts-shimmer{position:relative;overflow:hidden}
        .mts-shimmer::after{content:'';position:absolute;inset:0;
          background:linear-gradient(90deg,transparent,${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'},transparent);
          transform:translateX(-100%);animation:mtsShimmer 1.4s infinite}
        @keyframes mtsShimmer{100%{transform:translateX(100%)}}
        @keyframes mtsSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        .mts-spin{animation:mtsSpin 1s linear infinite}
        .mts-row:hover{background:${tk.surfaceAlt}}
        .mts-noscrollbar::-webkit-scrollbar{display:none}
        .mts-scroll::-webkit-scrollbar{width:6px;height:6px}
        .mts-scroll::-webkit-scrollbar-track{background:transparent}
        .mts-scroll::-webkit-scrollbar-thumb{background:${tk.borderStrong};border-radius:999px}
        .mts-scroll{scrollbar-width:thin;scrollbar-color:${tk.borderStrong} transparent}
        @media (prefers-reduced-motion:reduce){.mts-shimmer::after,.mts-spin{animation:none}}
      `}</style>

      {/* mobile navbar spacer */}
      {isMobile && <div style={{ height: 56 }} />}
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main style={{
        padding: isMobile ? '16px 14px 40px' : '32px 24px 48px',
        maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box',
      }}>

        {/* ---------- Header ---------- */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 14, marginBottom: isMobile ? 20 : 28,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 13, display: 'flex', flexShrink: 0,
              alignItems: 'center', justifyContent: 'center', background: tk.brandSoft,
            }}>
              <GitBranch style={{ width: 21, height: 21, color: tk.brand }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: isMobile ? 22 : 27, fontWeight: 800, letterSpacing: '-0.5px', margin: 0 }}>Journeys</h1>
              <p style={{ color: tk.textSoft, fontSize: isMobile ? 12 : 13, marginTop: 2, marginBottom: 0 }}>
                Automate multi-channel journeys from live and past behaviour
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <ThemeToggle tk={tk} theme={theme} setTheme={setTheme} isMobile={isMobile} />
            <button onClick={() => setBuildOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                flex: isMobile ? 1 : 'none',
                padding: isMobile ? '12px 18px' : '10px 18px',
                borderRadius: 12, fontSize: 13.5, fontWeight: 700,
                color: '#fff', border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${tk.brand}, ${theme === 'dark' ? '#9d8bff' : '#7b5bf0'})`,
              }}>
              <Plus style={{ width: 16, height: 16 }} /> Build journey
            </button>
          </div>
        </div>

        {/* ---------- View tabs ---------- */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: isMobile ? 16 : 20,
          background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
          borderRadius: 12, padding: 4, width: isMobile ? '100%' : 'fit-content',
        }}>
          {[
            { id: 'journeys', label: 'Journeys' },
            { id: 'reports', label: 'Fallback Reports' },
          ].map(t => (
            <button key={t.id} onClick={() => setView(t.id)}
              style={{
                flex: isMobile ? 1 : 'none',
                padding: isMobile ? '10px 14px' : '8px 18px',
                borderRadius: 9, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 700,
                background: view === t.id ? tk.surface : 'transparent',
                color: view === t.id ? tk.brand : tk.textSoft,
                boxShadow: view === t.id ? tk.shadow : 'none',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {view === 'reports' ? (
          <ReportsPanel tk={tk} theme={theme} isMobile={isMobile}
            summary={reportSummary} channels={reportChannels} trend={reportTrend}
            loading={reportLoading} onRefresh={fetchReports}
            onExport={exportReportCsv} exporting={exporting} />
        ) : (
        <>
        {/* ---------- Stats ---------- */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: isMobile ? 10 : 16, marginBottom: 18,
        }}>
          <StatCard label="Total journeys" value={stats.total}       color={tk.brand} tk={tk} loading={loading} isMobile={isMobile} />
          <StatCard label="Active"          value={stats.active}      color="#10b981" tk={tk} loading={loading} isMobile={isMobile} />
          <StatCard label="Enrollments"     value={stats.enrollments} color="#f59e0b" tk={tk} loading={loading} isMobile={isMobile} />
          <StatCard label="Channels in use" value={stats.channels}    color="#06b6d4" tk={tk} loading={loading} isMobile={isMobile} />
        </div>

        {/* ---------- Filters ---------- */}
        <div style={{
          background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 16,
          padding: 14, marginBottom: 18, boxShadow: tk.shadow,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: tk.textSoft }}>
            <Filter style={{ width: 14, height: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Filters
            </span>
            {activeFilterCount > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 800, minWidth: 18, height: 18, borderRadius: 999,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: tk.brand, color: '#fff', padding: '0 5px',
              }}>{activeFilterCount}</span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              {filtersActive && (
                <button onClick={resetFilters}
                  style={{ fontSize: 12, fontWeight: 700, color: tk.brand,
                    background: 'none', border: 'none', cursor: 'pointer', padding: isMobile ? '8px 4px' : 0 }}>
                  Clear all
                </button>
              )}
              {isMobile && (
                <button onClick={() => setFiltersOpen(o => !o)}
                  style={{ fontSize: 12, fontWeight: 700, color: tk.textSoft,
                    background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px',
                    display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {filtersOpen ? 'Hide' : 'Show'}
                  <ChevronRight style={{
                    width: 14, height: 14,
                    transform: filtersOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                    transition: 'transform .15s',
                  }} />
                </button>
              )}
            </div>
          </div>

          {(!isMobile || filtersOpen) && (
            <div style={{
              display: isMobile ? 'grid' : 'flex',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'none',
              flexWrap: 'wrap', gap: 10, marginTop: 12,
            }}>
              <select value={fChannel} onChange={e => setFChannel(e.target.value)} style={selStyle}>
                <option value="all">All channels</option>
                {visibleChannelKeys.map(k => <option key={k} value={k}>{CH[k].label}</option>)}
              </select>
              <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={selStyle}>
                <option value="all">Any status</option>
                {Object.keys(STATUS_MAP).map(k => <option key={k} value={k}>{STATUS_MAP[k].label}</option>)}
              </select>
              <select value={fTrigger} onChange={e => setFTrigger(e.target.value)}
                style={{ ...selStyle, gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                <option value="all">Live + past behaviour</option>
                <option value="live">Live behaviour</option>
                <option value="past">Past behaviour</option>
              </select>
              <input type="date" value={fFrom} onChange={e => setFFrom(e.target.value)}
                aria-label="From date" style={selStyle} />
              <input type="date" value={fTo} onChange={e => setFTo(e.target.value)}
                aria-label="To date" style={selStyle} />
            </div>
          )}
        </div>

        {/* ---------- Error ---------- */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            color: '#ef4444', padding: '14px 18px', borderRadius: 14, fontSize: 13.5, fontWeight: 600,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
          }}>
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>{error}</span>
            <button onClick={fetchJourneys}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 700, color: '#ef4444',
                background: 'none', border: 'none', cursor: 'pointer', padding: isMobile ? '6px 0' : 0 }}>
              <RefreshCw style={{ width: 12, height: 12 }} /> Retry
            </button>
          </div>
        )}

        {/* ---------- List ---------- */}
        <div style={{
          background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 18,
          overflow: 'hidden', boxShadow: tk.shadow,
        }}>
          <div style={{ padding: isMobile ? '14px 16px' : '16px 22px', borderBottom: `1px solid ${tk.border}` }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>All journeys</h2>
            <p style={{ color: tk.textSoft, fontSize: 12, marginTop: 2, marginBottom: 0 }}>
              {loading ? '—' : `${stats.total} journey${stats.total !== 1 ? 's' : ''}`}
            </p>
          </div>

          {loading ? (
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Array(4).fill(null).map((_, i) => <Skel key={i} tk={tk} h={52} />)}
            </div>
          ) : !error && journeys.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: isMobile ? '56px 20px' : '72px 20px' }}>
              <GitBranch style={{ width: 40, height: 40, color: tk.borderStrong, marginBottom: 12 }} />
              <p style={{ color: tk.textSoft, fontSize: 14, fontWeight: 700, margin: 0 }}>No journeys match these filters</p>
              <p style={{ color: tk.textFaint, fontSize: 12, marginTop: 4, marginBottom: 0, textAlign: 'center' }}>
                {filtersActive ? 'Try clearing filters, or build a new journey.' : 'Build your first multi-channel journey.'}
              </p>
            </div>
          ) : !error ? (
            isMobile ? (
              /* ---------- Mobile cards ---------- */
              <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {journeys.map((j) => (
                  <div key={j.id} onClick={() => openDetail(j.id)}
                    style={{ background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                      borderRadius: 14, padding: 14, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start',
                      justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontWeight: 700, fontSize: 14, margin: 0,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {titleize(j.name)}
                        </p>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 11, fontWeight: 600, color: tk.textSoft, marginTop: 3 }}>
                          {j.trigger_type === 'live'
                            ? <Zap style={{ width: 12, height: 12, color: '#f59e0b' }} />
                            : <History style={{ width: 12, height: 12, color: '#06b6d4' }} />}
                          {j.trigger_type === 'live' ? 'Live behaviour' : 'Past behaviour'}
                        </span>
                      </div>
                      <div style={{ flexShrink: 0 }}><StatusBadge status={j.status} tk={tk} /></div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                      {(j.channels || []).map(c => <ChannelPill key={c} channel={c} tk={tk} small />)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12, color: tk.textSoft }}>
                        {num(j.node_count)} nodes · {num(j.enrollments)} enrolled
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); handleTrigger(j.id); }}
                        disabled={triggering === j.id}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          fontSize: 12, fontWeight: 700, color: tk.brand,
                          border: `1px solid ${tk.brand}55`, background: 'transparent',
                          padding: '9px 14px', minHeight: 38, borderRadius: 10, cursor: 'pointer',
                          opacity: triggering === j.id ? 0.5 : 1, flexShrink: 0 }}>
                        {triggering === j.id
                          ? <RefreshCw className="mts-spin" style={{ width: 14, height: 14 }} />
                          : <Play style={{ width: 14, height: 14 }} />}
                        Trigger
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* ---------- Desktop table ---------- */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13.5, borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead>
                    <tr style={{ background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}` }}>
                      {['Journey', 'Trigger', 'Channels', 'Nodes', 'Enrollments', 'Status', ''].map((c, i) => (
                        <th key={i} style={{
                          textAlign: i === 6 ? 'right' : 'left', padding: '11px 18px',
                          fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.09em', color: tk.textFaint,
                        }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {journeys.map((j) => (
                      <tr key={j.id} className="mts-row"
                        style={{ borderBottom: `1px solid ${tk.border}`, cursor: 'pointer' }}
                        onClick={() => openDetail(j.id)}>
                        <td style={{ padding: '13px 18px', fontWeight: 700 }}>{titleize(j.name)}</td>
                        <td style={{ padding: '13px 18px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: 11, fontWeight: 700, color: tk.textSoft }}>
                            {j.trigger_type === 'live'
                              ? <Zap style={{ width: 13, height: 13, color: '#f59e0b' }} />
                              : <History style={{ width: 13, height: 13, color: '#06b6d4' }} />}
                            {j.trigger_type === 'live' ? 'Live' : 'Past'}
                          </span>
                        </td>
                        <td style={{ padding: '13px 18px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {(j.channels || []).slice(0, 4).map(c =>
                              <ChannelPill key={c} channel={c} tk={tk} small />)}
                            {(j.channels || []).length > 4 &&
                              <span style={{ fontSize: 11, color: tk.textFaint }}>
                                +{j.channels.length - 4}
                              </span>}
                          </div>
                        </td>
                        <td style={{ padding: '13px 18px' }}>
                          <span style={{ background: tk.brandSoft, color: tk.brand,
                            padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                            {num(j.node_count)}
                          </span>
                        </td>
                        <td style={{ padding: '13px 18px', fontWeight: 700, color: '#f59e0b' }}>
                          {num(j.enrollments)}
                        </td>
                        <td style={{ padding: '13px 18px' }}><StatusBadge status={j.status} tk={tk} /></td>
                        <td style={{ padding: '13px 18px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleTrigger(j.id)} disabled={triggering === j.id}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                              fontSize: 12, fontWeight: 700, color: tk.brand,
                              border: `1px solid ${tk.brand}55`, background: 'transparent',
                              padding: '6px 12px', borderRadius: 9, cursor: 'pointer',
                              opacity: triggering === j.id ? 0.5 : 1 }}>
                            {triggering === j.id
                              ? <RefreshCw className="mts-spin" style={{ width: 14, height: 14 }} />
                              : <Play style={{ width: 14, height: 14 }} />}
                            {triggering === j.id ? 'Triggering…' : 'Trigger'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
        </>
        )}
      </main>

      {/* Detail drawer */}
      {detail && (
        <DetailDrawer
          tk={tk} theme={theme} data={detail} loading={detailLoading}
          isMobile={isMobile} onClose={() => setDetail(null)}
        />
      )}

      {/* Build modal */}
      {buildOpen && (
        <BuildModal
          tk={tk} theme={theme} authHeaders={authHeaders} isMobile={isMobile}
          onClose={() => setBuildOpen(false)}
          onCreated={() => { setBuildOpen(false); fetchJourneys(); showToast('Journey created'); }}
          onError={(m) => showToast(m, 'red')}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/* Detail drawer — side sheet on desktop, full-screen on mobile        */
function DetailDrawer({ tk, theme, data, loading, isMobile, onClose }) {
  const isLoading = loading || data.loadingId;
  const nodes = data.nodes || [];
  const journey = data.journey;
  const enr = data.enrollment_summary || {};

  const [fallbackStats, setFallbackStats] = useState([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const hasConditionNode = nodes.some(n => n.node_type === 'condition');

  const authHeaders = () => {
    const token = localStorage.getItem('martech_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFallbackStatus = useCallback(async () => {
    if (!journey?.name) return;
    setFallbackLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/journeys/${encodeURIComponent(journey.name)}/fallback-status`,
        { headers: authHeaders() });
      const d = await r.json();
      if (d.success) setFallbackStats(d.stats || []);
    } catch { /* keep silent — non-critical panel */ }
    setFallbackLoading(false);
  }, [journey?.name]);

  useEffect(() => { if (hasConditionNode) fetchFallbackStatus(); }, [hasConditionNode, fetchFallbackStatus]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: tk.overlay }} onClick={onClose} />
      <div className="mts-scroll" style={{
        position: 'relative', zIndex: 10,
        width: '100%', maxWidth: isMobile ? '100%' : 560, height: '100%',
        background: tk.canvas,
        borderLeft: isMobile ? 'none' : `1px solid ${tk.border}`,
        overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
        boxSizing: 'border-box',
      }}>
        <div style={{
          position: 'sticky', top: 0, zIndex: 2, background: tk.canvas,
          borderBottom: `1px solid ${tk.border}`,
          padding: isMobile ? '14px 16px' : '18px 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: tk.text, margin: 0,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isLoading ? 'Loading…' : titleize(journey?.name)}
            </h2>
            {!isLoading && journey && (
              <p style={{ fontSize: 12, color: tk.textSoft, marginTop: 2, marginBottom: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {journey.trigger_type === 'live' ? 'Live behaviour' : 'Past behaviour'}
                {journey.trigger_event ? ` · ${titleize(journey.trigger_event)}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose}
            style={{ color: tk.textSoft, background: 'none', border: 'none', cursor: 'pointer',
              width: isMobile ? 42 : 32, height: isMobile ? 42 : 32, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div style={{ padding: isMobile ? '16px 14px calc(24px + env(safe-area-inset-bottom, 0px))' : 22 }}>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Array(4).fill(null).map((_, i) => <Skel key={i} tk={tk} h={80} />)}
            </div>
          ) : (
            <>
              {/* enrollment summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
                {[
                  { l: 'Enrolled', v: num(enr.total), c: tk.brand },
                  { l: 'Completed', v: num(enr.completed), c: '#10b981' },
                  { l: 'In progress', v: num(enr.total) - num(enr.completed), c: '#f59e0b' },
                ].map((s) => (
                  <div key={s.l} style={{ background: tk.surface, border: `1px solid ${tk.border}`,
                    borderRadius: 13, padding: isMobile ? 12 : 14 }}>
                    <p style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800, color: s.c, lineHeight: 1, margin: 0 }}>{s.v}</p>
                    <p style={{ fontSize: 11, color: tk.textSoft, marginTop: 5, marginBottom: 0, fontWeight: 600 }}>{s.l}</p>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.08em', color: tk.textFaint, marginBottom: 14 }}>
                Journey flow
              </p>

              {/* node graph */}
              <div>
                {nodes.map((n, idx) => {
                  const c = CH[n.channel] || { label: n.channel, Icon: GitBranch, color: tk.textFaint };
                  const { Icon } = c;
                  const isWait = n.node_type === 'wait';
                  return (
                    <div key={n.node_id}>
                      <div style={{ background: tk.surface, border: `1px solid ${tk.border}`,
                        borderRadius: 14, padding: isMobile ? 14 : 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: `${c.color}1a` }}>
                              {isWait
                                ? <Clock style={{ width: 17, height: 17, color: tk.textSoft }} />
                                : <Icon style={{ width: 17, height: 17, color: c.color }} />}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p style={{ fontWeight: 700, fontSize: 13.5, color: tk.text, margin: 0,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Step {n.step_order} · {isWait ? 'Wait' : c.label}
                              </p>
                              {n.template_ref && (
                                <p style={{ fontSize: 11.5, color: tk.textSoft, marginTop: 1, marginBottom: 0,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {titleize(n.template_ref)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}><StatusBadge status={n.status} tk={tk} /></div>
                        </div>

                        {/* node metrics */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {n.wait_hours > 0 && (
                            <Metric tk={tk} Icon={Clock} label="Wait"
                              value={n.wait_hours >= 24 ? `${Math.round(n.wait_hours / 24)}d` : `${n.wait_hours}h`} />
                          )}
                          {!isWait && (
                            <>
                              <Metric tk={tk} Icon={Send}
                                label={n.channel === 'whatsapp' && num(n.real_sent) === 0 ? 'Dry runs' : 'Sent'}
                                value={n.channel === 'whatsapp' && num(n.real_sent) === 0 ? num(n.dry_run) : num(n.real_sent)}
                                color={n.channel === 'whatsapp' && num(n.real_sent) === 0 ? '#f59e0b' : undefined} />
                              <Metric tk={tk} Icon={CheckCircle2} label="Delivered" value={num(n.delivered)} color="#10b981" />
                              <Metric tk={tk} Icon={MousePointer} label="Clicks" value={num(n.clicked)} color={tk.brand} />
                            </>
                          )}
                        </div>
                      </div>
                      {idx < nodes.length - 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
                          <ChevronRight style={{ width: 18, height: 18, color: tk.borderStrong, transform: 'rotate(90deg)' }} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {nodes.length === 0 && (
                  <p style={{ color: tk.textSoft, fontSize: 13 }}>This journey has no nodes yet.</p>
                )}
              </div>

              {/* Fallback Activity — live results from the Omnichannel Fallback engine */}
              {hasConditionNode && (
                <div style={{ marginTop: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.08em', color: tk.textFaint, margin: 0 }}>
                      Fallback activity
                    </p>
                    <button onClick={fetchFallbackStatus} disabled={fallbackLoading}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
                        color: tk.brand, background: 'none', border: 'none', cursor: 'pointer',
                        padding: isMobile ? '6px 4px' : 0, opacity: fallbackLoading ? 0.5 : 1 }}>
                      <RefreshCw className={fallbackLoading ? 'mts-spin' : ''} style={{ width: 12, height: 12 }} /> Refresh
                    </button>
                  </div>

                  {fallbackLoading && fallbackStats.length === 0 ? (
                    <Skel tk={tk} h={60} />
                  ) : fallbackStats.length === 0 ? (
                    <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 13,
                      padding: 16, textAlign: 'center' }}>
                      <p style={{ color: tk.textSoft, fontSize: 12.5, margin: 0 }}>
                        No evaluations yet. The engine checks every 15 minutes once an enrollment reaches its wait window.
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {fallbackStats.map((s, i) => {
                        const metColor = s.condition_result === true ? '#10b981'
                          : s.condition_result === false ? '#f59e0b' : tk.textFaint;
                        const chInfoS = CH[s.channel_used] || { label: s.channel_used, Icon: GitBranch, color: tk.textFaint };
                        const IconS = chInfoS.Icon;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 10, background: tk.surface, border: `1px solid ${tk.border}`,
                            borderRadius: 12, padding: '10px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: metColor, flexShrink: 0 }} />
                              <span style={{ fontSize: 12.5, fontWeight: 700, color: tk.text }}>Step {s.step_number}</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 11.5, color: tk.textSoft }}>
                                <IconS style={{ width: 12, height: 12, color: chInfoS.color }} />
                                {chInfoS.label}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              <span style={{ fontSize: 11, fontWeight: 700,
                                color: metColor }}>
                                {s.condition_result === true ? 'Condition met' : s.condition_result === false ? 'Fallback fired' : '—'}
                              </span>
                              <StatusBadge status={s.status} tk={tk} />
                              <span style={{ fontSize: 11, color: tk.textFaint, fontWeight: 700 }}>×{s.count}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/* Build modal — centered dialog on desktop, bottom sheet on mobile    */
function BuildModal({ tk, theme, authHeaders, isMobile, onClose, onCreated, onError }) {
  const { flags } = useFeatureFlags();
  const visibleChannelKeys = CHANNEL_KEYS.filter((k) => flags[CHANNEL_FLAG_MAP[k]] !== false);
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState('live');
  const [triggerEvent, setTriggerEvent] = useState(TRIGGER_EVENTS[0]);
  const [nodes, setNodes] = useState([
    { channel: 'email', node_type: 'message', wait_hours: 0, template_ref: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const [allTemplates, setAllTemplates] = useState([]);
  const [tmplLoading, setTmplLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setTmplLoading(true);
      try {
        const r = await fetch(`${API_BASE}/api/v1/compliance/templates`,
          { headers: authHeaders() });
        const d = await r.json();
        if (!cancelled && d.success) setAllTemplates(d.templates || []);
      } catch { /* silently degrade to empty */ }
      if (!cancelled) setTmplLoading(false);
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const templatesFor = (channel) =>
    allTemplates.filter(t => (t.channel || '').toLowerCase() === channel.toLowerCase());

  const CM_CHANNELS = ['whatsapp', 'email', 'sms'];
  const PENDING_CHANNELS = ['rcs', 'instagram'];
  const ADS_CHANNELS = ['facebook', 'google'];

  const addNode = () =>
    setNodes(ns => [...ns, { channel: 'email', node_type: 'message', wait_hours: 0, template_ref: '' }]);
  const removeNode = (i) => setNodes(ns => ns.filter((_, idx) => idx !== i));
  const patchNode = (i, patch) =>
    setNodes(ns => ns.map((n, idx) => idx === i ? { ...n, ...patch } : n));
  const changeChannel = (i, channel) => patchNode(i, { channel, template_ref: '' });

  const submit = async () => {
    if (!name.trim()) { onError('Give the journey a name'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/journeys`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), trigger_type: triggerType,
          trigger_event: triggerEvent,
          nodes: nodes.map(n => ({
            channel: n.channel, node_type: n.node_type,
            wait_hours: parseInt(n.wait_hours, 10) || 0,
            template_ref: (n.template_ref || '').trim() || null,
            ...(n.node_type === 'condition' ? {
              condition_metric: n.condition_metric || 'clicked',
              on_true_channel: n.on_true_channel || 'whatsapp',
              on_false_channel: n.on_false_channel || 'sms',
              evaluate_after_hours: parseInt(n.evaluate_after_hours, 10) || 48,
            } : {}),
          })),
        }),
      });
      const d = await r.json();
      if (r.ok && d.success !== false) onCreated();
      else onError(d.error || 'Could not create journey');
    } catch { onError('Network error'); }
    setSubmitting(false);
  };

  const inp = {
    background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.text,
    fontSize: isMobile ? 16 : 13, padding: isMobile ? '10px 12px' : '8px 11px',
    borderRadius: 10, outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  const lab = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em',
    color: tk.textFaint, marginBottom: 6, display: 'block',
  };

  const statusColor = (s) => {
    if (s === 'approved' || s === 'active') return '#10b981';
    if (s === 'draft') return '#f59e0b';
    return '#71717a';
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      padding: isMobile ? 0 : 16,
    }}>
      <div style={{ position: 'absolute', inset: 0, background: tk.overlay }} onClick={onClose} />
      <div className="mts-scroll" style={{
        position: 'relative', zIndex: 10, width: '100%',
        maxWidth: isMobile ? '100%' : 560,
        maxHeight: isMobile ? '94vh' : '90vh',
        overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
        background: tk.surface, border: `1px solid ${tk.border}`,
        borderRadius: isMobile ? '20px 20px 0 0' : 20,
        padding: isMobile ? '10px 16px calc(24px + env(safe-area-inset-bottom, 0px))' : 24,
        boxSizing: 'border-box',
      }}>
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <div style={{ width: 36, height: 4, borderRadius: 999, background: tk.border }} />
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: 11, display: 'flex', flexShrink: 0,
              alignItems: 'center', justifyContent: 'center', background: tk.brandSoft }}>
              <GitBranch style={{ width: 17, height: 17, color: tk.brand }} />
            </div>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: tk.text, margin: 0,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Build journey</h2>
          </div>
          <button onClick={onClose}
            style={{ color: tk.textSoft, background: 'none', border: 'none', cursor: 'pointer',
              width: isMobile ? 42 : 32, height: isMobile ? 42 : 32, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Journey name */}
          <div>
            <label style={lab}>Journey name *</label>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Win-back across channels" style={inp} />
          </div>

          {/* Trigger row */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lab}>Trigger on</label>
              <select value={triggerType} onChange={e => setTriggerType(e.target.value)} style={inp}>
                <option value="live">Live behaviour</option>
                <option value="past">Past behaviour</option>
              </select>
            </div>
            <div>
              <label style={lab}>Event</label>
              <select value={triggerEvent} onChange={e => setTriggerEvent(e.target.value)} style={inp}>
                {TRIGGER_EVENTS.map(ev => <option key={ev} value={ev}>{titleize(ev)}</option>)}
              </select>
            </div>
          </div>

          {/* Steps */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ ...lab, marginBottom: 0 }}>Steps</label>
              <button onClick={addNode}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontWeight: 700, color: tk.brand,
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: isMobile ? '8px 4px' : 0, minHeight: isMobile ? 36 : 'auto' }}>
                <Plus style={{ width: 14, height: 14 }} /> Add step
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {nodes.map((n, i) => {
                const isPending = PENDING_CHANNELS.includes(n.channel);
                const isAds = ADS_CHANNELS.includes(n.channel);
                const isWait = n.node_type === 'wait';
                const isCondition = n.node_type === 'condition';
                const chTemplates = CM_CHANNELS.includes(n.channel) ? templatesFor(n.channel) : [];
                const chInfo = CH[n.channel] || { label: n.channel, color: tk.textFaint };

                return (
                  <div key={i} style={{ background: tk.surfaceAlt,
                    border: `1px solid ${tk.border}`, borderRadius: 13, padding: isMobile ? 12 : 14 }}>

                    {/* Step header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 22, height: 22, borderRadius: 7, display: 'inline-flex', flexShrink: 0,
                          alignItems: 'center', justifyContent: 'center', fontSize: 11,
                          fontWeight: 800, color: '#fff', background: tk.brand,
                        }}>{i + 1}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: chInfo.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {chInfo.label}
                          {n.node_type === 'condition' && (
                            <GitMerge style={{ width: 12, height: 12, color: '#f59e0b' }} />
                          )}
                        </span>
                      </div>
                      {nodes.length > 1 && (
                        <button onClick={() => removeNode(i)}
                          style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer',
                            width: isMobile ? 38 : 26, height: isMobile ? 38 : 26, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                          <Trash2 style={{ width: 14, height: 14 }} />
                        </button>
                      )}
                    </div>

                    {/* Channel + node type */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={{ ...lab, marginBottom: 4 }}>Channel</label>
                        <select value={n.channel}
                          onChange={e => changeChannel(i, e.target.value)} style={inp}>
                          {visibleChannelKeys.map(k => (
                            <option key={k} value={k}>{CH[k].label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ ...lab, marginBottom: 4 }}>Type</label>
                        <select value={n.node_type}
                          onChange={e => patchNode(i, { node_type: e.target.value })} style={inp}>
                          {NODE_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Wait hours — always shown */}
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ ...lab, marginBottom: 4 }}>Wait before this step (hours)</label>
                      <input type="number" min="0" value={n.wait_hours}
                        onChange={e => patchNode(i, { wait_hours: e.target.value })}
                        placeholder="0 = send immediately" style={inp} />
                    </div>

                    {/* Template selector — only for message-send nodes */}
                    {!isWait && !isCondition && (
                      <div>
                        <label style={{ ...lab, marginBottom: 4 }}>
                          Template
                          {tmplLoading && (
                            <span style={{ color: tk.textFaint, fontWeight: 400,
                              textTransform: 'none', marginLeft: 6 }}>loading…</span>
                          )}
                        </label>

                        {isAds ? (
                          <div style={{
                            padding: '9px 12px', borderRadius: 10, fontSize: 12,
                            color: tk.textSoft, fontWeight: 600,
                            background: tk.surface, border: `1px solid ${tk.border}`,
                          }}>
                            {chInfo.label} syncs this journey's segment as a custom audience — no message template needed for this step.
                          </div>
                        ) : isPending ? (
                          <div style={{
                            padding: '9px 12px', borderRadius: 10, fontSize: 12,
                            color: '#f59e0b', fontWeight: 600,
                            background: 'rgba(245,158,11,0.08)',
                            border: '1px solid rgba(245,158,11,0.2)',
                          }}>
                            Templates for {chInfo.label} are not yet in Content Manager.
                            Add them there first, then they'll appear here.
                          </div>
                        ) : chTemplates.length === 0 && !tmplLoading ? (
                          <div style={{
                            padding: '9px 12px', borderRadius: 10, fontSize: 12,
                            color: tk.textSoft, fontWeight: 600,
                            background: tk.surface, border: `1px solid ${tk.border}`,
                          }}>
                            No {chInfo.label} templates in Content Manager yet.
                          </div>
                        ) : (
                          <select value={n.template_ref}
                            onChange={e => patchNode(i, { template_ref: e.target.value })}
                            style={inp}>
                            <option value="">— select a template —</option>
                            {chTemplates.map(t => (
                              <option key={t.id} value={t.name}>
                                {t.name}
                                {t.status ? ` (${t.status})` : ''}
                                {t.category ? ` · ${t.category}` : ''}
                              </option>
                            ))}
                          </select>
                        )}

                        {/* Selected template preview */}
                        {n.template_ref && (() => {
                          const found = allTemplates.find(t => t.name === n.template_ref);
                          if (!found) return null;
                          return (
                            <div style={{
                              marginTop: 8, padding: '9px 12px', borderRadius: 10,
                              background: tk.canvas, border: `1px solid ${tk.border}`,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, padding: '2px 7px',
                                  borderRadius: 999, background: `${statusColor(found.status)}1a`,
                                  color: statusColor(found.status),
                                }}>
                                  {found.status}
                                </span>
                                {found.category && (
                                  <span style={{ fontSize: 10, color: tk.textFaint,
                                    fontWeight: 600 }}>{found.category}</span>
                                )}
                              </div>
                              <p style={{ fontSize: 12, color: tk.textSoft,
                                lineHeight: 1.5, margin: 0, wordBreak: 'break-word' }}>
                                {found.content}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Condition node — branch based on how the user responded to the previous step */}
                    {isCondition && (
                      <div>
                        <label style={{ ...lab, marginBottom: 4 }}>Based on user response</label>
                        <select value={n.condition_metric || 'clicked'}
                          onChange={e => patchNode(i, { condition_metric: e.target.value })}
                          style={{ ...inp, marginBottom: 10 }}>
                          <option value="opened">Opened / Read</option>
                          <option value="clicked">Clicked a link</option>
                          <option value="converted">Converted (purchase / booking)</option>
                          <option value="no_response_48h">No response after window</option>
                        </select>

                        <div style={{ marginBottom: 10 }}>
                          <label style={{ ...lab, marginBottom: 4 }}>Evaluate after (hours)</label>
                          <input type="number" min="1" value={n.evaluate_after_hours ?? 48}
                            onChange={e => patchNode(i, { evaluate_after_hours: e.target.value })}
                            placeholder="48" style={inp} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={{ ...lab, marginBottom: 4, color: '#10b981' }}>If yes → send via</label>
                            <select value={n.on_true_channel || 'whatsapp'}
                              onChange={e => patchNode(i, { on_true_channel: e.target.value })} style={inp}>
                              {visibleChannelKeys.map(k => <option key={k} value={k}>{CH[k].label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ ...lab, marginBottom: 4, color: '#f59e0b' }}>If no / fallback → send via</label>
                            <select value={n.on_false_channel || 'sms'}
                              onChange={e => patchNode(i, { on_false_channel: e.target.value })} style={inp}>
                              {visibleChannelKeys.map(k => <option key={k} value={k}>{CH[k].label}</option>)}
                            </select>
                          </div>
                        </div>

                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10,
                          padding: '9px 12px', borderRadius: 10, fontSize: 11.5, fontWeight: 600,
                          color: '#10b981', background: 'rgba(16,185,129,0.08)',
                          border: '1px solid rgba(16,185,129,0.2)',
                        }}>
                          <CheckCircle2 style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} />
                          <span>Omnichannel Fallback is live: after {n.evaluate_after_hours || 48}h, the system checks this metric automatically and routes to the fallback channel if unmet. View results in the journey's detail view under "Fallback Activity".</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: 10, paddingTop: 4 }}>
            <button onClick={onClose}
              style={{ flex: 1, padding: isMobile ? '13px' : '10px', borderRadius: 12, fontSize: 13.5,
                fontWeight: 700, background: tk.surfaceAlt,
                border: `1px solid ${tk.border}`, color: tk.textSoft, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={submit} disabled={submitting || !name.trim()}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: isMobile ? '13px' : '10px', borderRadius: 12, fontSize: 13.5,
                fontWeight: 700, color: '#fff', border: 'none', cursor: 'pointer',
                opacity: (submitting || !name.trim()) ? 0.5 : 1,
                background: `linear-gradient(135deg, ${tk.brand}, ${theme === 'dark' ? '#9d8bff' : '#7b5bf0'})` }}>
              {submitting
                ? <RefreshCw className="mts-spin" style={{ width: 16, height: 16 }} />
                : <Plus style={{ width: 16, height: 16 }} />}
              {submitting ? 'Creating…' : 'Create journey'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}